export const DEFAULT_CEFR_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
export const DEFAULT_LEARNING_LANGUAGE_CODE = 'es';
export const DEFAULT_CONTENT_FALLBACK_LANGUAGE_CODES = ['en', 'es'];

export const LEARNING_LANGUAGES = [
  {
    code: 'es',
    name: 'Spanish',
    nativeName: 'Espa\u00F1ol',
    flag: '\uD83C\uDDEA\uD83C\uDDF8',
    flagPath: '/flags/s/ES.svg',
    flagUrl: 'https://flagcdn.com/w80/es.png',
    aliases: ['es', 'spanish', 'espanol', 'espa\u00F1ol', 'castellano'],
    levels: DEFAULT_CEFR_LEVELS,
    defaultUiLanguage: 'es',
    onboardingOrder: 1,
    placementTestVariant: 'spanish',
    contentPaths: {
      episodes: 'languages/es/content/episodes',
      exercises: 'languages/es/content/exercises',
      gradedReaders: 'languages/es/content/gradedReaders',
      practiceByEpisode: 'languages/es/content/practiceByEpisode',
      config: 'languages/es/config',
    },
    resourceVariants: {
      sayMoreQuestions: 'es',
    },
    features: {
      placementTest: true,
      lessons: true,
      exercises: true,
      gradedReaders: true,
      voice: true,
      sayMore: true,
      notifications: true,
    },
    menuTheme: {
      bg: '#AA151B',
      bgHover: '#8a1016',
      border: '#F1BF00',
      text: '#FFFFFF',
    },
  },
  {
    code: 'en',
    name: 'English',
    nativeName: 'English',
    flag: '\uD83C\uDDEC\uD83C\uDDE7',
    flagPath: '/flags/s/GB-UKM.svg',
    flagUrl: 'https://flagcdn.com/w80/gb.png',
    aliases: ['en', 'english'],
    levels: DEFAULT_CEFR_LEVELS,
    defaultUiLanguage: 'en',
    onboardingOrder: 2,
    placementTestVariant: 'default',
    contentPaths: {
      episodes: 'languages/en/content/episodes',
      exercises: 'languages/en/content/exercises',
      gradedReaders: 'languages/en/content/gradedReaders',
      practiceByEpisode: 'languages/en/content/practiceByEpisode',
      config: 'languages/en/config',
    },
    resourceVariants: {
      sayMoreQuestions: 'en',
    },
    features: {
      placementTest: true,
      lessons: true,
      exercises: true,
      gradedReaders: true,
      voice: true,
      sayMore: true,
      notifications: true,
    },
    menuTheme: {
      bg: '#012169',
      bgHover: '#001a4d',
      border: '#C8102E',
      text: '#FFFFFF',
    },
  },
  {
    code: 'no',
    name: 'Norwegian',
    nativeName: 'Norsk',
    flag: '\uD83C\uDDF3\uD83C\uDDF4',
    flagPath: '/flags/s/NO.svg',
    flagUrl: 'https://flagcdn.com/w80/no.png',
    aliases: ['no', 'nb', 'nn', 'norwegian', 'norsk', 'bokm\u00E5l', 'bokmål', 'nynorsk'],
    levels: DEFAULT_CEFR_LEVELS,
    defaultUiLanguage: 'en',
    onboardingOrder: 3,
    placementTestVariant: 'norwegian',
    contentPaths: {
      episodes: 'languages/no/content/episodes',
      exercises: 'languages/no/content/exercises',
      gradedReaders: 'languages/no/content/gradedReaders',
      practiceByEpisode: 'languages/no/content/practiceByEpisode',
      config: 'languages/no/config',
    },
    resourceVariants: {
      sayMoreQuestions: 'no',
    },
    features: {
      placementTest: true,
      lessons: true,
      exercises: true,
      gradedReaders: true,
      voice: true,
      sayMore: true,
      notifications: true,
    },
    menuTheme: {
      bg: '#BA0C2F',
      bgHover: '#960a26',
      border: '#00205B',
      text: '#FFFFFF',
    },
  },
];

export const AVAILABLE_LEARNING_LANGUAGES = [...LEARNING_LANGUAGES].sort(
  (left, right) => left.onboardingOrder - right.onboardingOrder
);

export const LEARNING_LANGUAGE_BY_CODE = AVAILABLE_LEARNING_LANGUAGES.reduce((accumulator, language) => {
  accumulator[language.code] = language;
  return accumulator;
}, {});

const LEARNING_LANGUAGE_ALIAS_MAP = AVAILABLE_LEARNING_LANGUAGES.reduce((accumulator, language) => {
  [language.code, ...(language.aliases || [])].forEach((alias) => {
    accumulator[normalizeAlias(alias)] = language.code;
  });
  return accumulator;
}, {});

function normalizeAlias(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/_/g, '-')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

export function resolveLearningLanguageCode(input) {
  if (typeof input !== 'string') return null;

  const normalized = normalizeAlias(input);
  if (!normalized) return null;

  if (LEARNING_LANGUAGE_ALIAS_MAP[normalized]) {
    return LEARNING_LANGUAGE_ALIAS_MAP[normalized];
  }

  const baseCode = normalized.split('-')[0];
  if (LEARNING_LANGUAGE_ALIAS_MAP[baseCode]) {
    return LEARNING_LANGUAGE_ALIAS_MAP[baseCode];
  }

  return /^[a-z]{2,3}$/.test(baseCode) ? baseCode : null;
}

export function normalizeLearningLanguageCode(
  input,
  fallbackCode = DEFAULT_LEARNING_LANGUAGE_CODE
) {
  return resolveLearningLanguageCode(input) || fallbackCode;
}

export function getLearningLanguage(code) {
  const resolvedCode = normalizeLearningLanguageCode(code);
  return LEARNING_LANGUAGE_BY_CODE[resolvedCode] || LEARNING_LANGUAGE_BY_CODE[DEFAULT_LEARNING_LANGUAGE_CODE];
}

export function isSupportedLearningLanguageCode(code) {
  const resolvedCode = resolveLearningLanguageCode(code);
  return Boolean(resolvedCode && LEARNING_LANGUAGE_BY_CODE[resolvedCode]);
}

export function getPlacementTestVariant(code) {
  return getLearningLanguage(code).placementTestVariant || 'default';
}

export function getSayMoreQuestionVariant(code) {
  return getLearningLanguage(code).resourceVariants?.sayMoreQuestions || 'en';
}

export function getLearningLanguageTranslationKey(code) {
  const resolvedCode = normalizeLearningLanguageCode(code, DEFAULT_LEARNING_LANGUAGE_CODE);

  switch (resolvedCode) {
    case 'en':
      return 'english';
    case 'es':
      return 'spanish';
    case 'no':
      return 'norwegian';
    default:
      return null;
  }
}
